import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useT } from "@/lib/i18n";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Download, Mail, BookMarked, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Manual() {
  const t = useT();
  const { user } = useAuth();
  const [md, setMd] = useState("");
  const [loading, setLoading] = useState(true);
  const [emailing, setEmailing] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/manual/markdown");
        setMd(data.markdown || "");
      } catch (e) {
        toast.error(e.response?.data?.detail || "Failed to load manual");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleDownload = async () => {
    try {
      const r = await api.get("/manual/download", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "LaSovita-Ledger-Manual.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Download failed");
    }
  };

  const handleEmail = async () => {
    setEmailing(true);
    try {
      const { data } = await api.post("/manual/email", {});
      toast.success(`${t("manual.emailSent")} → ${data.to}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Email failed");
    } finally {
      setEmailing(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="manual-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="font-mono text-[10px] tracking-widest text-[#A78BFA] flex items-center gap-2">
            <BookMarked className="w-3 h-3" /> {t("manual.subtitle")}
          </div>
          <h2 className="font-display text-2xl font-bold tracking-tight">{t("manual.title")}</h2>
          <p className="text-sm text-[#94A3B8] mt-1 max-w-2xl">{t("manual.intro")}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={handleDownload}
            data-testid="manual-download-btn"
            className="px-4 py-2 rounded-md bg-[#007AFF] hover:bg-[#0066D6] text-white text-sm font-medium flex items-center gap-2 transition-colors"
          >
            <Download className="w-4 h-4" /> {t("manual.downloadPdf")}
          </button>
          <button
            onClick={handleEmail}
            disabled={emailing}
            data-testid="manual-email-btn"
            className="px-4 py-2 rounded-md bg-[#0B1221] border border-[#1E293B] hover:border-[#334155] text-sm font-medium flex items-center gap-2 transition-colors disabled:opacity-60"
          >
            {emailing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
            {t("manual.emailMe")}
            {user?.email && <span className="font-mono text-[10px] text-[#94A3B8] hidden md:inline">· {user.email}</span>}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="ledger-card p-12 text-center text-[#94A3B8]" data-testid="manual-loading">{t("common.loading")}</div>
      ) : (
        <article className="ledger-card p-6 lg:p-10 manual-prose" data-testid="manual-content">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{md}</ReactMarkdown>
        </article>
      )}
    </div>
  );
}
